import { useNavigation } from '@react-navigation/core'
import { isError } from '@tanstack/react-query'
import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useToast } from '../toast'
import { useAuth } from '../useAuth'

import {
  useGetCountriesList,
  useGetStatesList,
  useGetCitiesList,
  useGetUser,
  useUpdateUser,
} from '~query-hooks'
import { getAddressByPostalCode, getAddressByCoordinates } from '~services'
import { requestLocationPermission, isGeolocationAvailable } from '~utils/locationPermission'
import { Routes } from '~types/routesNames'
import { UpdateUserDetailsFormValues, InterestType } from '~types/updateUserDetailsForm'

export const useUpdateUserDetailsForm = () => {
  const { navigate } = useNavigation<UserProfileScreenProps['navigation']>()
  const { t } = useTranslation()
  const { showToast } = useToast()

  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasAnyFieldData, setHasAnyFieldData] = useState(false)
  const [isLocationLoading, setIsLocationLoading] = useState(false)
  const lastResetUserId = useRef<string | null>(null)

  const { profileUpdated } = useAuth()
  const { user, refetchUser } = useGetUser()
  const { updateUser } = useUpdateUser()
  const { countriesList: countriesResponse } = useGetCountriesList()
  const countryRecords = useMemo(
    () => countriesResponse?.countries ?? [],
    [countriesResponse?.countries]
  )
  const normalizeCountryValue = useCallback(
    (value: string) => {
      if (!value) {
        return ''
      }
      const matchedById = countryRecords.find((country) => country.iso2 === value)
      if (matchedById) {
        return matchedById.id?.toString() ?? String(matchedById.id)
      }
      const matchedByIso = countryRecords.find(
        (country) => country.iso2?.toUpperCase() === value.toUpperCase()
      )
      return matchedByIso?.id?.toString() ?? String(matchedByIso?.id ?? value)
    },
    [countryRecords]
  )

  const { basicInfo, email = '' } = user || {}
  const {
    birthDate = '',
    gender = '',
    countryId: storedCountryId = '',
    location: legacyCountry = '',
    stateId = '',
    state = '',
    cityId = '',
    city = '',
    zipCode = '',
    interests = [],
  } = basicInfo || {}

  const normalizedCountryId = useMemo(
    () => normalizeCountryValue(storedCountryId || legacyCountry),
    [legacyCountry, normalizeCountryValue, storedCountryId]
  )

  const defaultValues: UpdateUserDetailsFormValues = useMemo(
    () => ({
      email,
      birthDate: birthDate
        ? typeof birthDate === 'string'
          ? parseInt(birthDate, 10)
          : birthDate
        : undefined,
      gender,
      countryId: normalizedCountryId,
      location: normalizedCountryId,
      stateId,
      state,
      cityId,
      city,
      zipCode,
      interests: interests as InterestType[],
    }),
    [
      birthDate,
      gender,
      normalizedCountryId,
      stateId,
      state,
      cityId,
      city,
      zipCode,
      interests,
      email,
    ]
  )

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    getValues,
    watch,
    reset,
    clearErrors,
  } = useForm<UpdateUserDetailsFormValues>({
    mode: 'onChange',
    defaultValues,
  })

  // Clear any existing errors when form initializes since all fields are optional
  useEffect(() => {
    clearErrors()
  }, [clearErrors])

  // Clear errors when form is reset
  useEffect(() => {
    if (user && email) {
      clearErrors()
    }
  }, [user, email, clearErrors])

  // Watch all form fields to check if any have data (excluding email)
  const watchedFields = watch([
    'birthDate',
    'gender',
    'countryId',
    'stateId',
    'state',
    'cityId',
    'city',
    'zipCode',
    'interests',
  ])
  const watchedCountryId = watch('countryId')
  const watchedStateId = watch('stateId')
  const watchedState = watch('state')
  const watchedCity = watch('city')
  const previousCountryIdRef = useRef<string>('')
  const previousStateIdRef = useRef<string>('')
  const previousZipRef = useRef<string>('')
  const zipLookupAbortControllerRef = useRef<AbortController | null>(null)
  const pendingStateNameRef = useRef<string>('')
  const pendingCityNameRef = useRef<string>('')
  const locationPromptShownRef = useRef(false)

  // Fetch states and cities based on selected country and state
  const { states: statesList } = useGetStatesList(watchedCountryId, {
    enabled: Boolean(watchedCountryId),
  })
  const { cities: citiesList } = useGetCitiesList(watchedStateId, {
    enabled: Boolean(watchedStateId),
  })

  // Check if any field has data (excluding email)
  useEffect(() => {
    const hasData = watchedFields.some((field) => {
      if (Array.isArray(field)) {
        return field.length > 0 // For interests array
      }
      return field && field !== '' // For other fields
    })
    setHasAnyFieldData(hasData)
  }, [watchedFields])

  useEffect(() => {
    if (!countryRecords.length) {
      return
    }
    const currentCountryId = getValues('countryId')
    if (!currentCountryId) {
      return
    }
    const normalized = normalizeCountryValue(String(currentCountryId))
    if (normalized && normalized !== String(currentCountryId)) {
      setValue('countryId', normalized, { shouldValidate: false, shouldDirty: true })
      setValue('location', normalized, { shouldValidate: false, shouldDirty: true })
    }
  }, [countryRecords, getValues, normalizeCountryValue, setValue])

  useEffect(() => {
    if (previousCountryIdRef.current && previousCountryIdRef.current !== (watchedCountryId || '')) {
      setValue('stateId', '', { shouldValidate: false })
      setValue('state', '', { shouldValidate: false })
      setValue('cityId', '', { shouldValidate: false })
      setValue('city', '', { shouldValidate: false })
      pendingStateNameRef.current = ''
      pendingCityNameRef.current = ''
    }

    previousCountryIdRef.current = watchedCountryId || ''
  }, [watchedCountryId, setValue])

  useEffect(() => {
    if (previousStateIdRef.current && previousStateIdRef.current !== (watchedStateId || '')) {
      setValue('cityId', '', { shouldValidate: false })
      setValue('city', '', { shouldValidate: false })
      pendingCityNameRef.current = ''
    }

    previousStateIdRef.current = watchedStateId || ''
  }, [watchedStateId, setValue])

  // Match state name to stateId when states list loads
  useEffect(() => {
    if (!statesList?.length) {
      return
    }

    // Check both pending ref and current form value
    const stateNameToMatch = (pendingStateNameRef.current || getValues('state') || '')
      .trim()
      .toLowerCase()
    if (!stateNameToMatch) {
      return
    }

    const matchedState = statesList.find((state) => {
      const label = state.label?.trim().toLowerCase() || ''

      // Remove common words like "region", "of", "state", "province" for better matching
      const removeCommonWords = (str: string) => {
        return str
          .replace(/\b(region|of|state|province|district|county|area)\b/gi, '')
          .trim()
          .replace(/\s+/g, ' ')
      }

      const normalizedLabel = removeCommonWords(label)
      const normalizedStateName = removeCommonWords(stateNameToMatch)

      // Use "like" query - partial/fuzzy matching instead of exact match
      return (
        label.includes(stateNameToMatch) ||
        stateNameToMatch.includes(label) ||
        normalizedLabel.includes(normalizedStateName) ||
        normalizedStateName.includes(normalizedLabel) ||
        // Check if all words from one string are present in the other
        normalizedStateName.split(/\s+/).every((word) => normalizedLabel.includes(word)) ||
        normalizedLabel.split(/\s+/).every((word) => normalizedStateName.includes(word))
      )
    })

    if (matchedState) {
      const currentStateId = getValues('stateId')
      if (currentStateId !== matchedState.value) {
        setValue('stateId', matchedState.value, { shouldValidate: false, shouldDirty: true })
        setValue('state', matchedState.label, { shouldValidate: false, shouldDirty: true })
      }
      // Only clear the ref after successful match to allow re-matching if zip changes
      if (matchedState) {
        pendingStateNameRef.current = ''
      }
    }
  }, [statesList, setValue, getValues, watchedCountryId, watchedState])

  // Match city name to cityId when cities list loads
  useEffect(() => {
    if (!citiesList?.length) {
      return
    }

    // Check both pending ref and current form value
    const cityNameToMatch = (pendingCityNameRef.current || getValues('city') || '')
      .trim()
      .toLowerCase()
    if (!cityNameToMatch) {
      return
    }

    // Prefer exact match; avoid cityNameToMatch.includes(label) so "Bengaluru" doesn't match "Alur"
    const matchedCity = citiesList.find((city) => {
      const label = city.label?.trim().toLowerCase() || ''
      return (
        label === cityNameToMatch ||
        label.includes(cityNameToMatch) ||
        label.toLowerCase().replace(/\s+/g, ' ') === cityNameToMatch.toLowerCase().replace(/\s+/g, ' ')
      )
    })

    if (matchedCity) {
      const currentCityId = getValues('cityId')
      if (currentCityId !== matchedCity.value) {
        setValue('cityId', matchedCity.value, { shouldValidate: false, shouldDirty: true })
        setValue('city', matchedCity.label, { shouldValidate: false, shouldDirty: true })
      }
      // Only clear the ref after successful match to allow re-matching if zip changes
      if (matchedCity) {
        pendingCityNameRef.current = ''
      }
    }
  }, [citiesList, setValue, getValues, watchedStateId, watchedCity])

  // Reset form when user data changes (only once per user)
  useEffect(() => {
    if (user && email && user.id !== lastResetUserId.current) {
      lastResetUserId.current = user.id
      reset({
        email,
        birthDate: birthDate
          ? typeof birthDate === 'string'
            ? parseInt(birthDate, 10)
            : birthDate
          : undefined,
        gender,
        countryId: normalizedCountryId,
        location: normalizedCountryId,
        stateId,
        state,
        cityId,
        city,
        zipCode,
        interests: interests as InterestType[],
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, email, reset])

  // Auto-fill location: browser handles permission prompt; we show loading while fetching
  const runAutoFillFromLocation = useCallback(async () => {
    setIsLocationLoading(true)
    try {
      const position = await requestLocationPermission()
      if (!position) return

      const { latitude, longitude } = position.coords
      const address = await getAddressByCoordinates(latitude, longitude)
      if (!address) return

      if (address.countryCode) {
        const mappedCountryId = normalizeCountryValue(address.countryCode)
        if (mappedCountryId) {
          setValue('countryId', mappedCountryId, { shouldValidate: false, shouldDirty: true })
          setValue('location', mappedCountryId, { shouldValidate: false, shouldDirty: true })
        }
        setValue('stateId', '', { shouldValidate: false })
        setValue('state', '', { shouldValidate: false })
        setValue('cityId', '', { shouldValidate: false })
        setValue('city', '', { shouldValidate: false })
        pendingStateNameRef.current = ''
        pendingCityNameRef.current = ''
      }
      if (address.state) {
        setValue('state', address.state, { shouldValidate: false, shouldDirty: true })
        pendingStateNameRef.current = address.state
      }
      if (address.city) {
        setValue('city', address.city, { shouldValidate: false, shouldDirty: true })
        pendingCityNameRef.current = address.city
      }
      if (address.postalCode) {
        setValue('zipCode', address.postalCode, { shouldValidate: false, shouldDirty: true })
      }
    } catch (err) {
      console.warn('Auto-fill location failed', err)
    } finally {
      setIsLocationLoading(false)
    }
  }, [normalizeCountryValue, setValue])

  useEffect(() => {
    if (locationPromptShownRef.current) return
    const currentCountryId = getValues('countryId')
    const currentCity = getValues('city')
    const currentState = getValues('state')
    const currentZipCode = getValues('zipCode')
    if (currentCountryId || currentCity || currentState || currentZipCode) return
    if (!isGeolocationAvailable() || countryRecords.length === 0 || !user?.id) return

    locationPromptShownRef.current = true
    runAutoFillFromLocation()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryRecords.length, user?.id])

  const validateInterests = (interests: string[]) => {
    // All fields are now optional, so always return true
    return true
  }

  const onSubmit = async (basicInfo: UpdateUserDetailsFormValues) => {
    try {
      setIsSubmitting(true)
      setError('')

      // No validation needed since all fields are optional
      const { location: _location, ...payload } = basicInfo
      await updateUser({
        basicInfo: payload,
      })

      await refetchUser()
      navigate(Routes.UserProfile, { showRewardModal: !profileUpdated })
    } catch (e) {
      if (isError(e)) {
        setError(e.message)
      } else {
        setError(t('errors.something_went_wrong'))
      }
      showToast({ description: error, variant: 'danger' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const resolveZipCode = useCallback(
    async (zip: string) => {
      if (zipLookupAbortControllerRef.current) {
        zipLookupAbortControllerRef.current.abort()
      }

      const controller = new AbortController()
      zipLookupAbortControllerRef.current = controller

      try {
        const address = await getAddressByPostalCode(zip, controller.signal)
        if (!address) {
          return
        }

        // Set country first, which will clear dependent fields
        if (address.countryCode) {
          const mappedCountryId = normalizeCountryValue(address.countryCode)
          if (mappedCountryId) {
            setValue('countryId', mappedCountryId, { shouldValidate: false, shouldDirty: true })
            setValue('location', mappedCountryId, { shouldValidate: false, shouldDirty: true })
          }
          // Clear state and city fields and reset pending refs to allow re-matching
          setValue('stateId', '', { shouldValidate: false })
          setValue('state', '', { shouldValidate: false })
          setValue('cityId', '', { shouldValidate: false })
          setValue('city', '', { shouldValidate: false })
          pendingStateNameRef.current = ''
          pendingCityNameRef.current = ''
        }

        // Then set state and city after country is set
        // Store state and city names in refs for later matching when lists load
        if (address.state) {
          setValue('state', address.state, { shouldValidate: false, shouldDirty: true })
          pendingStateNameRef.current = address.state
        }
        if (address.city) {
          setValue('city', address.city, { shouldValidate: false, shouldDirty: true })
          pendingCityNameRef.current = address.city
        }
      } catch (error) {
        // Silently handle abort errors
        if ((error as Error).name !== 'AbortError') {
          // Zip code lookup failed
        }
      }
    },
    [normalizeCountryValue, setValue, countryRecords.length]
  )

  const handleZipBlur = useCallback(() => {
    const currentZip = getValues('zipCode')?.trim()

    if (!currentZip || currentZip.length < 3) {
      previousZipRef.current = ''
      return
    }

    if (currentZip === previousZipRef.current) {
      return
    }

    previousZipRef.current = currentZip
    resolveZipCode(currentZip)
  }, [getValues, resolveZipCode])

  const handleZipChange = useCallback(
    (value: string) => {
      const normalized = value.trim()

      if (!normalized || normalized.length < 3) {
        return
      }

      if (normalized === previousZipRef.current) {
        return
      }

      previousZipRef.current = normalized
      resolveZipCode(normalized)
    },
    [resolveZipCode]
  )

  useEffect(() => {
    return () => {
      zipLookupAbortControllerRef.current?.abort()
    }
  }, [])

  return {
    submit: handleSubmit(onSubmit),
    isSubmitting,
    setIsSubmitting,
    control,
    errors,
    error,
    setValue,
    getValues,
    watch,
    validateInterests,
    reset,
    hasAnyFieldData,
    clearErrors,
    handleZipChange,
    handleZipBlur,
    isLocationLoading,
  }
}
